package com.banyan.flightviewer.web.components

import com.banyan.flightviewer.data.AircraftTypeDatabase
import com.banyan.flightviewer.data.AirportDatabase
import com.banyan.flightviewer.state.AircraftData
import com.banyan.flightviewer.web.components.map.altitudeColor as altitudeColorShared
import com.banyan.flightviewer.web.components.map.attachFlightPathLayer
import com.banyan.flightviewer.web.components.map.attachWeatherRadar
import com.banyan.flightviewer.web.dom.bindFlow
import com.banyan.flightviewer.web.dom.button
import com.banyan.flightviewer.web.dom.div
import com.banyan.flightviewer.web.dom.onClick
import com.banyan.flightviewer.web.platform.L
import com.banyan.flightviewer.web.platform.LeafletMap
import com.banyan.flightviewer.web.platform.Marker
import com.banyan.flightviewer.web.platform.TileLayer
import com.banyan.flightviewer.web.state.WebAppContainer
import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.Element
import org.w3c.dom.HTMLDivElement
import org.w3c.dom.events.MouseEvent
import kotlin.js.json
import kotlin.math.PI
import kotlin.math.atan2
import kotlin.math.cos
import kotlin.math.sqrt

/**
 * Leaflet map view. Ports the imperative logic from
 * `desktop/src/main/resources/leaflet/map.html` to Kotlin/JS.
 *
 * Behavior parity targets (see map.html for reference):
 *  - Aircraft markers as rotated SVG, altitude-coloured, with callsign labels
 *  - Click a marker -> selectFlight(hex)
 *  - Selected aircraft drawn larger with an orange glow
 *  - 10s linear interpolation between position updates
 *  - Tile-style switcher (dark/standard/satellite/terrain)
 *  - "Recenter" button when the selected aircraft drifts off-screen
 *  - Major airports rendered as yellow square markers (zoom >= 8)
 *  - Right-click sets the user's monitoring location
 */
fun mountMapView(parent: Element, app: WebAppContainer) {
    val mapEl = parent.div(className = "fv-map")
    val mapContainer = mapEl.div { id = "fv-map-container" }
    mapContainer.asDynamic().style.width = "100%"
    mapContainer.asDynamic().style.height = "100%"

    val recenterBtn = mapEl.button(className = "fv-recenter-btn") {
        textContent = "Recenter Map"
    }
    val controls = mapEl.div(className = "fv-map-controls") { }

    // Weather button lives above the tile-style buttons so the toggle is
    // discoverable (active state uses a distinct color so it doesn't look
    // like a selected tile style).
    val weatherBtn = controls.button(className = "fv-map-btn fv-map-btn--weather") {
        textContent = "Weather"
    }

    val styleButtons = mutableMapOf<String, Element>()
    // Pairs of (id, label). `hybrid` is satellite imagery with country /
    // city / road labels overlaid via Esri's reference layer.
    val tileStyles = listOf(
        "dark" to "Dark",
        "standard" to "Standard",
        "satellite" to "Satellite",
        "hybrid" to "Hybrid",
        "terrain" to "Terrain",
    )
    tileStyles.forEach { (id, label) ->
        styleButtons[id] = controls.button(className = "fv-map-btn") {
            textContent = label
        }
    }

    // Defer init until the container has been laid out (height needs to be > 0
    // for Leaflet's tile loader to compute a viewport). One animation frame is
    // usually enough.
    window.requestAnimationFrame {
        initMap(mapEl, mapContainer, recenterBtn, weatherBtn, styleButtons, app)
    }
}

private fun initMap(
    mapEl: Element,
    mapContainer: HTMLDivElement,
    recenterBtn: Element,
    weatherBtn: Element,
    styleButtons: Map<String, Element>,
    app: WebAppContainer,
) {
    val s = app.appState.settings.value
    val map = L.map(
        mapContainer,
        json("minZoom" to 7, "maxZoom" to 18, "preferCanvas" to true),
    ).setView(arrayOf(s.lat, s.lon), 10)

    // Each style is one or more tile layers, stacked in draw order.
    // "hybrid" stacks Esri's place/boundary reference layer on top of
    // satellite imagery so you get labels with the imagery underneath.
    val satellite = L.tileLayer(
        "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
        json("attribution" to "&copy; Esri", "maxZoom" to 18),
    )
    val hybridBase = L.tileLayer(
        "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
        json("attribution" to "&copy; Esri", "maxZoom" to 18),
    )
    val hybridLabels = L.tileLayer(
        "https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}",
        json("maxZoom" to 18, "pane" to "overlayPane"),
    )
    val tileLayers: Map<String, List<TileLayer>> = mapOf(
        "dark" to listOf(
            L.tileLayer(
                "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png",
                json("attribution" to "&copy; OpenStreetMap &copy; CARTO", "maxZoom" to 20),
            ),
        ),
        "standard" to listOf(
            L.tileLayer(
                "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
                json("attribution" to "&copy; OpenStreetMap contributors", "maxZoom" to 18),
            ),
        ),
        "satellite" to listOf(satellite),
        "hybrid" to listOf(hybridBase, hybridLabels),
        "terrain" to listOf(
            L.tileLayer(
                "https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png",
                json("attribution" to "&copy; OpenTopoMap", "maxZoom" to 17),
            ),
        ),
    )

    var currentStyle = "hybrid"   // satellite imagery + place/boundary labels
    tileLayers[currentStyle]!!.forEach { layer: TileLayer -> layer.addTo(map) }
    fun activate(style: String) {
        styleButtons.forEach { (k, btn) ->
            btn.className = if (k == style) "fv-map-btn active" else "fv-map-btn"
        }
    }
    activate(currentStyle)
    styleButtons.forEach { (style, btn) ->
        btn.onClick {
            if (style == currentStyle) return@onClick
            tileLayers[currentStyle]!!.forEach { layer: TileLayer -> map.removeLayer(layer) }
            tileLayers[style]!!.forEach { layer: TileLayer -> layer.addTo(map) }
            currentStyle = style
            activate(style)
        }
    }

    // Fit to settings radius
    val latOffset = s.radiusKm / 111.0
    val lonOffset = s.radiusKm / (111.0 * cos(s.lat * PI / 180))
    val pad = 1.02
    map.fitBounds(
        arrayOf(
            arrayOf(s.lat - latOffset * pad, s.lon - lonOffset * pad),
            arrayOf(s.lat + latOffset * pad, s.lon + lonOffset * pad),
        ),
    )

    // User location marker (blue dot + outer pulse ring). Keep strong refs
    // so we can reposition them when settings.lat/lon change (e.g. after
    // "Set as my location" right-click, or auto-detect on boot).
    val userDot = L.circleMarker(
        arrayOf(s.lat, s.lon),
        json(
            "radius" to 7,
            "color" to "#ffffff",
            "weight" to 2,
            "fillColor" to "#4285F4",
            "fillOpacity" to 1,
        ),
    ).bindPopup("Your Location")
    userDot.addTo(map)

    val userPulse = L.circleMarker(
        arrayOf(s.lat, s.lon),
        json(
            "radius" to 14,
            "color" to "#4285F4",
            "weight" to 1,
            "fillColor" to "#4285F4",
            "fillOpacity" to 0.15,
            "interactive" to false,
        ),
    )
    userPulse.addTo(map)

    // React to any settings-lat/lon change (right-click, Settings dialog,
    // auto-detect on boot) by moving the user marker to the new spot.
    // We also pan the map there when the user appears to be at/near the
    // old viewport center (so explicit "Set as my location" shows visible
    // confirmation), but leave the viewport alone when the user has panned
    // off to explore somewhere unrelated.
    app.appState.scope.bindFlow(app.appState.settings) { updated ->
        userDot.setLatLng(arrayOf(updated.lat, updated.lon))
        userPulse.setLatLng(arrayOf(updated.lat, updated.lon))
    }

    // Right-click to set "my location". Save settings (this persists
    // lat/lon to localStorage via SettingsManager -> SettingsStore, AND
    // updates appState.settings), then recenter the map so the user sees
    // visible confirmation, then wake the monitoring loop.
    //
    // On the NEXT visit the persisted lat/lon are read back by
    // `SettingsManager.loadAllSettings()` before the map mounts, so the
    // initial setView/fitBounds open the map at this location.
    map.on("contextmenu") { e ->
        // Leaflet's LatLng exposes lat/lng as numeric properties, but the
        // external binding types them as `dynamic`. Go through asDynamic +
        // toDouble() explicitly so we end up with real Kotlin Doubles
        // regardless of how the JS side hands them back.
        val latNum = (e.latlng.lat.asDynamic() as Number).toDouble()
        val lonNum = (e.latlng.lng.asDynamic() as Number).toDouble()
        val latStr = latNum.asDynamic().toFixed(6) as String
        val lonStr = lonNum.asDynamic().toFixed(6) as String
        if (!window.confirm("Set $latStr, $lonStr as your location?")) return@on

        val current = app.appState.settings.value
        val updated = current.copy(lat = latNum, lon = lonNum)
        try {
            app.settingsManager.saveSettings(updated)
        } catch (t: Throwable) {
            // localStorage can throw QuotaExceededError or be unavailable in
            // strict-privacy browser modes. Surface it so the user knows
            // why the pin didn't stick for next visit.
            app.appState.setStatusMessage("Couldn't save location: ${t.message ?: "storage unavailable"}")
            kotlin.js.console.warn("saveSettings failed", t)
            return@on
        }
        map.setView(arrayOf(latNum, lonNum), map.getZoom(), json("animate" to true))
        app.appState.requestImmediateRefresh()
        app.appState.setStatusMessage("Location saved: $latStr, $lonStr")
        kotlin.js.console.info("Location saved to localStorage: $latStr, $lonStr")
    }

    // Airport markers layer (visible at zoom >= 8)
    val airportLayer = L.layerGroup()
    val airports = AirportDatabase.getAirportsWithLocations()
    airports.forEach { apt ->
        val svg = """
            <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' width='18' height='18'>
              <rect x='1' y='1' width='22' height='22' rx='4' fill='#1a1a2e' stroke='#FFD600' stroke-width='1.5'/>
              <path d='M12 4 L14 9 L20 11 L14 11.5 L14 16 L16.5 18 L7.5 18 L10 16 L10 11.5 L4 11 L10 9 Z' fill='#FFD600' stroke='none'/>
            </svg>
        """.trimIndent()
        val icon = L.divIcon(
            json(
                "html" to svg,
                "className" to "fv-aircraft-marker",
                "iconSize" to arrayOf(18, 18),
                "iconAnchor" to arrayOf(9, 9),
            ),
        )
        val marker = L.marker(
            arrayOf(apt.lat, apt.lon),
            json("icon" to icon, "zIndexOffset" to 2000),
        )
        marker.bindTooltip(
            "<b>${apt.code}</b><br>${apt.name}",
            json("direction" to "top", "offset" to arrayOf(0, -6)),
        )
        marker.on("click") { ev ->
            // ev.originalEvent.stopPropagation() must be called as a method
            // (not via ?.invoke()) so the DOM keeps the right `this` binding.
            val orig = ev.originalEvent
            if (orig != null) orig.stopPropagation()
            app.appState.selectAirport(apt.code)
        }
        airportLayer.addLayer(marker)
    }

    fun checkAirportVisibility() {
        if (map.getZoom() >= 8) {
            if (!map.hasLayer(airportLayer)) map.addLayer(airportLayer)
        } else {
            if (map.hasLayer(airportLayer)) map.removeLayer(airportLayer)
        }
    }
    map.on("zoomend", { checkAirportVisibility() })
    checkAirportVisibility()

    // Hide labels at low zoom
    fun updateLabelVisibility() {
        if (map.getZoom() >= 12) document.body!!.classList.remove("fv-low-zoom")
        else document.body!!.classList.add("fv-low-zoom")
    }
    map.on("zoomend", { updateLabelVisibility() })
    updateLabelVisibility()

    // Aircraft marker pool
    val markers = mutableMapOf<String, Marker>()
    val dataStore = mutableMapOf<String, AircraftData>()
    val animState = mutableMapOf<String, AnimSegment>()
    val pulses = mutableMapOf<String, dynamic>()  // emergency pulse ring per hex
    var animFrameId: Int? = null

    fun startAnimLoop() {
        if (animFrameId != null) return
        fun tick(ts: Double) {
            val now = ts
            var anyActive = false
            animState.forEach { (h, seg) ->
                val t = ((now - seg.startTime) / seg.duration).coerceIn(0.0, 1.0)
                val lat = seg.startLat + (seg.targetLat - seg.startLat) * t
                val lon = seg.startLon + (seg.targetLon - seg.startLon) * t
                markers[h]?.setLatLng(arrayOf(lat, lon))
                pulses[h]?.setLatLng(arrayOf(lat, lon))
                if (t < 1.0) anyActive = true
            }
            if (anyActive) animFrameId = window.requestAnimationFrame(::tick)
            else animFrameId = null
        }
        animFrameId = window.requestAnimationFrame(::tick)
    }

    var snapNext = false

    fun renderMarkers(list: List<AircraftData>, selectedHex: String?) {
        val now = window.performance.now()
        val seenHexes = mutableSetOf<String>()
        val doSnap = snapNext
        snapNext = false

        list.forEach { ac ->
            seenHexes.add(ac.hex)
            val prev = dataStore[ac.hex]
            val prevDisplayHdg = prev?.heading ?: ac.heading
            val displayHeading: Double = if (isOnGround(ac) && prev != null) {
                bearingFromDelta(prev.lat, prev.lon, ac.lat, ac.lon) ?: prevDisplayHdg
            } else ac.heading
            // Stash the displayHeading on the AircraftData copy in the local store
            dataStore[ac.hex] = ac.copy(heading = displayHeading)

            val isSelected = ac.hex == selectedHex
            val icon = createMarkerIcon(ac, isSelected, displayHeading)
            val tooltip = createTooltipHtml(ac)

            val existing = markers[ac.hex]
            if (existing != null && prev != null && !doSnap) {
                // Update icon + tooltip; animate position
                existing.setIcon(icon)
                existing.setTooltipContent(tooltip)
                val cur = animState[ac.hex]
                val startLat = if (cur != null) {
                    val t = ((now - cur.startTime) / cur.duration).coerceIn(0.0, 1.0)
                    cur.startLat + (cur.targetLat - cur.startLat) * t
                } else prev.lat
                val startLon = if (cur != null) {
                    val t = ((now - cur.startTime) / cur.duration).coerceIn(0.0, 1.0)
                    cur.startLon + (cur.targetLon - cur.startLon) * t
                } else prev.lon
                animState[ac.hex] = AnimSegment(startLat, startLon, ac.lat, ac.lon, now, 10_000.0)
            } else {
                if (existing != null) map.removeLayer(existing)
                val marker = L.marker(
                    arrayOf(ac.lat, ac.lon),
                    json("icon" to icon, "zIndexOffset" to 1000),
                )
                marker.bindTooltip(tooltip, json("direction" to "top", "offset" to arrayOf(0, -12)))
                val hex = ac.hex
                marker.on("click") { ev ->
                    // ev.originalEvent.stopPropagation() must be called as a method
            // (not via ?.invoke()) so the DOM keeps the right `this` binding.
            val orig = ev.originalEvent
            if (orig != null) orig.stopPropagation()
                    app.appState.selectFlight(hex)
                }
                marker.addTo(map)
                markers[ac.hex] = marker
                animState[ac.hex] = AnimSegment(ac.lat, ac.lon, ac.lat, ac.lon, now, 10_000.0)
            }

            // Emergency pulse
            val existingPulse = pulses[ac.hex]
            if (existingPulse != null) {
                map.removeLayer(existingPulse)
                pulses.remove(ac.hex)
            }
            val emerColor = EMERGENCY_COLOURS[ac.squawk]
            if (emerColor != null) {
                val pulse = L.circleMarker(
                    arrayOf(ac.lat, ac.lon),
                    json(
                        "radius" to 18,
                        "color" to emerColor,
                        "weight" to 2,
                        "fillColor" to emerColor,
                        "fillOpacity" to 0.15,
                        "className" to "fv-emergency-pulse",
                        "interactive" to false,
                    ),
                )
                pulse.addTo(map)
                pulses[ac.hex] = pulse.asDynamic()
            }
        }

        // Remove stale markers
        markers.keys.toList().forEach { hex ->
            if (hex !in seenHexes) {
                val m = markers[hex]
                if (m != null) map.removeLayer(m)
                markers.remove(hex)
                dataStore.remove(hex)
                animState.remove(hex)
                val p = pulses[hex]
                if (p != null) map.removeLayer(p)
                pulses.remove(hex)
            }
        }

        startAnimLoop()
    }

    // Drive marker updates from the filtered list so search/filter toggles
    // hide markers in addition to table rows.
    app.appState.scope.bindFlow(app.uiState.filteredAircraftList) { list ->
        renderMarkers(list, app.appState.selectedCallsign.value)
    }

    // Re-render when selection changes (so selected highlight updates)
    app.appState.scope.bindFlow(app.appState.selectedCallsign) { _ ->
        renderMarkers(app.uiState.filteredAircraftList.value, app.appState.selectedCallsign.value)
    }

    // Recenter button visibility + click
    fun updateRecenterBtn() {
        val selected = app.appState.selectedCallsign.value
        val ac = selected?.let { dataStore[it] }
        if (selected == null || ac == null) {
            recenterBtn.asDynamic().style.display = "none"
            return
        }
        val bounds = map.getBounds()
        val inView = bounds.contains(arrayOf(ac.lat, ac.lon))
        val center = map.getCenter()
        val latSpan = bounds.getNorth() - bounds.getSouth()
        val lonSpan = bounds.getEast() - bounds.getWest()
        val offCenter = kotlin.math.abs(ac.lat - center.lat) > latSpan * 0.3 ||
            kotlin.math.abs(ac.lon - center.lng) > lonSpan * 0.3
        recenterBtn.asDynamic().style.display = if (!inView || offCenter) "block" else "none"
    }
    map.on("moveend", { updateRecenterBtn() })
    map.on("zoomend", { updateRecenterBtn() })
    app.appState.scope.bindFlow(app.appState.selectedCallsign) { updateRecenterBtn() }

    recenterBtn.addEventListener("click", { _: org.w3c.dom.events.Event ->
        val selected = app.appState.selectedCallsign.value
        val ac = selected?.let { dataStore[it] } ?: return@addEventListener
        map.setView(arrayOf(ac.lat, ac.lon), map.getZoom(), json("animate" to true))
        recenterBtn.asDynamic().style.display = "none"
    })

    // Pan the map to a target. On mobile with the bottom-sheet overlay
    // up, a naive `setView(lat, lon)` centers the target at the
    // container midpoint, which lands under the sheet — the user sees
    // the area NORTH of the target and thinks the pan didn't happen.
    //
    // Catches for initial deep-link boot (where initMap runs inside
    // the first requestAnimationFrame and the map's internal state may
    // not yet reflect final container dimensions):
    //  - If the container has zero size, defer to the next frame and
    //    retry. Without this, `setView` silently no-ops on some mobile
    //    engines.
    //  - `invalidateSize()` so Leaflet recomputes its viewport before
    //    any positioning math.
    //  - setView first (always fires the pan to the correct area),
    //    then on mobile-with-overlay shift the view up so the target
    //    ends up above the sheet.
    fun panMapTo(lat: Double, lon: Double, zoom: Int) {
        if (mapContainer.clientWidth == 0 || mapContainer.clientHeight == 0) {
            window.requestAnimationFrame { panMapTo(lat, lon, zoom) }
            return
        }
        map.invalidateSize()
        val offsetPx = mobileOverlayOffsetPx(mapEl)
        if (offsetPx == 0) {
            map.setView(arrayOf(lat, lon), zoom, json("animate" to true))
            return
        }
        // Instant setView + animated panBy: gives a smooth shift into
        // position instead of animating into a hidden spot first.
        map.setView(arrayOf(lat, lon), zoom, json("animate" to false))
        // panBy(0, -N) translates the map pane up by N pixels, so the
        // target (currently at container center) slides up by N pixels
        // on screen -- above the bottom-sheet overlay.
        map.asDynamic().panBy(
            arrayOf(0, -offsetPx),
            json("animate" to true),
        )
    }

    // When selectedAirport changes, pan the map to that airport. If the
    // user tapped the marker, they're already centred (no-op); if they
    // tapped a favorited airport in the sidebar, the map jumps there.
    // Zoom up to at least 10 so runways + nearby aircraft are visible.
    app.appState.scope.bindFlow(app.appState.selectedAirport) { code ->
        if (code == null) return@bindFlow
        val apt = AirportDatabase.getAirportsWithLocations()
            .firstOrNull { it.code.equals(code, ignoreCase = true) }
            ?: return@bindFlow
        val targetZoom = maxOf(map.getZoom(), 10)
        panMapTo(apt.lat, apt.lon, targetZoom)
    }

    // Generic map-focus requests (currently driven by the deep-link share
    // flow: "I arrived at /flight/<hex>, pan the map to the plane's
    // current position"). We consume the request by clearing the flow
    // so the same coords can fire again later if needed.
    app.appState.scope.bindFlow(app.uiState.mapFocusRequest) { focus ->
        if (focus == null) return@bindFlow
        val targetZoom = focus.zoom ?: maxOf(map.getZoom(), 10)
        panMapTo(focus.lat, focus.lon, targetZoom)
        app.uiState.clearMapFocus()
    }

    // Push the viewport's center + computed radius into uiState on every
    // moveend/zoomend. The monitoring loop reads this so aircraft fetches
    // follow the user's panning instead of staying pinned to their home
    // settings. See desktop's `MapManager.getMapQueryArea()` for the same
    // pattern -- buffer 10% past the farthest visible corner, cap at 250nm
    // so we don't ask upstream for the whole hemisphere when zoomed way
    // out.
    fun pushQueryArea() {
        val c = map.getCenter()
        val b = map.getBounds()
        val corners = arrayOf(
            b.getNorth() to b.getWest(),
            b.getNorth() to b.getEast(),
            b.getSouth() to b.getWest(),
            b.getSouth() to b.getEast(),
        )
        var maxKm = 0.0
        for ((lat, lon) in corners) {
            val d = com.banyan.flightviewer.util.GeoUtils.haversineDistance(c.lat, c.lng, lat, lon)
            if (d > maxKm) maxKm = d
        }
        val radiusKm = (maxKm * 1.1).coerceAtMost(MAP_QUERY_MAX_RADIUS_KM)
        app.uiState.setMapQueryArea(
            com.banyan.flightviewer.web.state.MapQueryArea(c.lat, c.lng, radiusKm),
        )
    }
    // Initial push so the first monitoring cycle uses viewport coords.
    pushQueryArea()
    map.on("moveend", { pushQueryArea() })
    map.on("zoomend", { pushQueryArea() })
    // Nudge the loop to refresh on explicit panning instead of waiting out
    // the update interval. Simple debounce so rapid drags produce one hit.
    var moveRefreshHandle: Int? = null
    fun scheduleMoveRefresh() {
        moveRefreshHandle?.let { window.clearTimeout(it) }
        moveRefreshHandle = window.setTimeout({
            app.appState.requestImmediateRefresh()
            moveRefreshHandle = null
        }, 500)
    }
    map.on("moveend", { scheduleMoveRefresh() })
    map.on("zoomend", { scheduleMoveRefresh() })

    // Selected-flight polyline (altitude-banded) + RainViewer overlay.
    attachFlightPathLayer(map, app)
    attachWeatherRadar(map, weatherBtn, app.appState.scope)

    // If the layout changes (e.g. mobile tab switch), tell Leaflet to recompute
    // its viewport. Hooking on window resize is good enough for now.
    window.addEventListener("resize", { map.invalidateSize() })
}

// === Helpers =============================================================

/**
 * Cap on the viewport-derived query radius. 250 nautical miles (~463 km) --
 * matches desktop, protects upstream APIs from whole-hemisphere queries
 * when the user zooms way out. Above this radius ADS-B Exchange can return
 * 10k+ aircraft which melts the browser anyway.
 */
private const val MAP_QUERY_MAX_RADIUS_KM: Double = 463.0

/**
 * Viewport breakpoint (inclusive) at which the mobile layout applies --
 * matches the `@media (max-width: 768px)` block in app.css that
 * switches the sidebar into a bottom-sheet overlay.
 */
private const val MOBILE_BREAKPOINT_PX: Int = 768

/**
 * Pixel offset to shift the map's effective center south of the pan
 * target, or 0 when no adjustment is needed. A positive return value
 * means the target appears that many pixels ABOVE the container center
 * on screen -- clear of the bottom-sheet overlay that sits over the
 * lower ~60vh of the map on mobile.
 *
 * Returns 0 when:
 *  - viewport wider than the mobile breakpoint (no overlay layout),
 *  - no selection (so no sheet is up), or
 *  - mobile tab is "list" (sidebar is full-height, not a bottom sheet).
 */
private fun mobileOverlayOffsetPx(mapEl: Element): Int {
    if (window.innerWidth > MOBILE_BREAKPOINT_PX) return 0
    val root = mapEl.closest(".fv-app") ?: return 0
    if (root.getAttribute("data-has-selection") != "true") return 0
    if (root.getAttribute("data-mobile-tab") != "map") return 0
    // Roughly: sheet = 60vh, target should land in the top 40vh's
    // center (≈20vh from top). To move target from 50vh (container
    // midpoint) up to 20vh, shift the effective map center south by
    // 30vh pixels = 30% of window.innerHeight. Clamp so we never try
    // to shift more than the container can actually accommodate.
    val shift = (window.innerHeight * 0.30).toInt()
    return shift.coerceAtLeast(1)
}

private data class AnimSegment(
    val startLat: Double,
    val startLon: Double,
    val targetLat: Double,
    val targetLon: Double,
    val startTime: Double,
    val duration: Double,
)

private fun isOnGround(ac: AircraftData): Boolean {
    if (ac.altitude == "ground") return true
    val alt = ac.altitude.toDoubleOrNull() ?: return false
    return alt <= 0 && ac.speed < 30
}

private fun bearingFromDelta(prevLat: Double, prevLon: Double, lat: Double, lon: Double): Double? {
    val cosLat = cos(lat * PI / 180)
    val dLat = lat - prevLat
    val dLon = (lon - prevLon) * cosLat
    val distM = sqrt(dLat * dLat + dLon * dLon) * 111_000
    if (distM < 10) return null
    val bearing = atan2(dLon, dLat) * 180 / PI
    return (bearing + 360) % 360
}

private val EMERGENCY_COLOURS = mapOf(
    "7700" to "#FF0000",
    "7600" to "#FF0000",
    "7500" to "#FF0000",
)
private val EMERGENCY_LABELS = mapOf(
    "7700" to "EMERGENCY",
    "7600" to "RADIO FAIL",
    "7500" to "HIJACK",
)

// Delegates to the shared helper in [map/AltitudeColor.kt] so this file and
// the flight-path layer agree on altitude->color mapping without duplication.
private fun altitudeColor(altitude: String): String = altitudeColorShared(altitude)

private val ICON_SVGS = mapOf(
    AircraftTypeDatabase.Category.JET to
        "<path d='M12 2 L14 10 L22 14 L14 14 L14 20 L17 22 L7 22 L10 20 L10 14 L2 14 L10 10 Z'/>",
    AircraftTypeDatabase.Category.WIDEBODY to
        "<path d='M12 1 L15 10 L23 13 L23 15 L15 14 L15 20 L18 22 L6 22 L9 20 L9 14 L1 15 L1 13 L9 10 Z'/>",
    AircraftTypeDatabase.Category.TURBOPROP to
        "<path d='M12 3 L14 11 L20 14 L14 14 L13 20 L16 22 L8 22 L11 20 L10 14 L4 14 L10 11 Z'/>",
    AircraftTypeDatabase.Category.LIGHT to
        "<path d='M12 4 L13 11 L18 14 L13 14 L13 19 L15 21 L9 21 L11 19 L11 14 L6 14 L11 11 Z'/>",
    AircraftTypeDatabase.Category.HELICOPTER to
        "<circle cx='12' cy='13' r='4'/><line x1='4' y1='7' x2='20' y2='7' stroke-width='2'/>" +
            "<line x1='12' y1='7' x2='12' y2='9'/><line x1='12' y1='17' x2='12' y2='21'/>" +
            "<line x1='8' y1='21' x2='16' y2='21' stroke-width='1.5'/>",
    AircraftTypeDatabase.Category.MILITARY to
        "<path d='M12 1 L15 9 L22 12 L15 13 L14 18 L18 22 L12 20 L6 22 L10 18 L9 13 L2 12 L9 9 Z'/>",
    AircraftTypeDatabase.Category.UNKNOWN to
        "<path d='M12 2 L14 10 L22 14 L14 14 L14 20 L17 22 L7 22 L10 20 L10 14 L2 14 L10 10 Z'/>",
)

private fun createMarkerIcon(ac: AircraftData, selected: Boolean, displayHeading: Double): com.banyan.flightviewer.web.platform.DivIcon {
    val isEmergency = ac.squawk in EMERGENCY_COLOURS
    val color = when {
        selected -> "#FF6D00"
        isEmergency -> EMERGENCY_COLOURS[ac.squawk]!!
        else -> altitudeColor(ac.altitude)
    }
    val glow = when {
        selected -> "filter:drop-shadow(0 0 3px #FF6D00) drop-shadow(0 0 5px rgba(255,109,0,0.5));"
        isEmergency -> "filter:drop-shadow(0 0 3px ${EMERGENCY_COLOURS[ac.squawk]});"
        else -> ""
    }
    val sz = if (selected) 28 else 22
    val category = AircraftTypeDatabase.getCategory(ac.aircraftType)
    val svgBody = ICON_SVGS[category] ?: ICON_SVGS[AircraftTypeDatabase.Category.JET]!!

    val svg = """
        <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' width='$sz' height='$sz'
             style='transform:rotate(${displayHeading}deg);cursor:pointer;$glow'
             fill='$color' stroke='$color' stroke-width='0.5'>
          $svgBody
        </svg>
    """.trimIndent()

    val callsign = ac.callsign
    val html = "<div style='position:relative;width:${sz}px;height:${sz}px;'>" +
        svg +
        "<span class='fv-aircraft-label'>$callsign</span></div>"

    return L.divIcon(
        json(
            "html" to html,
            "className" to "fv-aircraft-marker",
            "iconSize" to arrayOf(sz, sz),
            "iconAnchor" to arrayOf(sz / 2, sz / 2),
        ),
    )
}

private fun createTooltipHtml(ac: AircraftData): String {
    val emer = EMERGENCY_LABELS[ac.squawk]
    val emergencyLine = if (emer != null) {
        "<br><span style='color:red;font-weight:bold;'>${ac.squawk} $emer</span>"
    } else ""
    return "<b>${ac.callsign}</b><br>" +
        "Alt: ${ac.altitude} ft<br>" +
        "Spd: ${ac.speed.toInt()} kt<br>" +
        "Hdg: ${ac.heading.toInt()}\u00B0$emergencyLine"
}
